import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { adminApi } from '../services/api';

interface OrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ORG_STATUSES = ['active', 'inactive', 'trial'];

const emptyForm = { displayName: '', name: '', domain: '', status: 'active' };

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setError(null);
  };

  const handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = event.target.value;
    const autoName = displayName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData((prev) => ({ ...prev, displayName, name: autoName }));
    setError(null);
  };

  const reset = () => {
    setFormData({ ...emptyForm });
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return;
    }
    if (!formData.name.trim()) {
      setError('Organization ID is required');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(formData.name.trim())) {
      setError('Organization ID may only contain lowercase letters, numbers, and dashes');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adminApi.createOrganization({
        name: formData.name.trim(),
        displayName: formData.displayName.trim(),
        status: formData.status,
        domains: formData.domain.trim() ? [formData.domain.trim()] : [],
      });
      reset();
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Organization</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Display Name"
            value={formData.displayName}
            onChange={handleDisplayNameChange}
            required
            fullWidth
            autoFocus
            disabled={loading}
            helperText="Human-readable name for the organization"
          />
          <TextField
            label="Organization ID"
            value={formData.name}
            onChange={handleChange('name')}
            required
            fullWidth
            disabled={loading}
            helperText="Auto-generated from display name (e.g. 'My Company' → 'my-company')"
          />
          <TextField
            label="Domain"
            value={formData.domain}
            onChange={handleChange('domain')}
            fullWidth
            disabled={loading}
            placeholder="example.com"
            helperText="Optional: primary email domain for this organization"
          />
          <TextField
            label="Status"
            select
            value={formData.status}
            onChange={handleChange('status')}
            fullWidth
            disabled={loading}
          >
            {ORG_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Organization'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationDialog;
